import "bootstrap-icons/font/bootstrap-icons.css";
import "./ChipInput.scss";
import React, { useState } from "react";
import { copyTextToClipboard } from "../../utils";
import {
  calculateTagCount,
  getByteLength,
  isYoutubeTagByteLengthValid,
  YOUTUBE_TAG_BYTE_MAX,
} from "@/utils/mediautils/CommonFunctions";
import { toast } from "../Toast/Toast";
interface ChipInputProps {
  label?: string;
  chipCharacterLimit?: number;
  suggestions?: string[];
  value: string[];
  placeholder?: string;
  handleChipChange: (chip: string | string[], action: string) => void;
  maxWidth?: string;
  className?: string;
  error?: any;
  touched?: boolean;
  onBlur?: any;
  disabled?: boolean;
  showRequired?: boolean;
  totalCharacterLimit?: number;
  labelCheckbox?: boolean;
  labelCheckboxChecked?: boolean;
  onLabelCheckboxChange?: (checked: boolean) => void;
  showAsterisk?: boolean;
  disableLabelCheckbox?: boolean;
  readOnlyMode?: boolean;
  asteriskClassName?: string;
  isYoutube?: boolean;
  fixedLayout?: boolean;
}

export const ChipInput: React.FC<ChipInputProps> = ({
  suggestions = [],
  label,
  value,
  placeholder,
  handleChipChange,
  error,
  onBlur,
  showRequired,
  disabled = false,
  totalCharacterLimit = 500,
  labelCheckbox = false,
  labelCheckboxChecked = false,
  onLabelCheckboxChange,
  showAsterisk = false,
  disableLabelCheckbox = false,
  readOnlyMode = false,
  asteriskClassName,
  isYoutube = false,
  className = "",
  fixedLayout = false,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isFocused, setIsFocused] = useState(false);

  const committedChars = Array.isArray(value)
    ? calculateTagCount(value, isYoutube).tagCount
    : 0;
  const totalCharsUsed = committedChars;

  const addChip = (newChip: string) => {
    const chips = newChip
      .split(",")
      .map((chip) => chip.trim())
      .filter((chip) => chip.length > 0);

    let remainingChars = totalCharacterLimit - committedChars;
    const chipsToAdd: string[] = [];

    for (const chip of chips) {
      if (isYoutube && !isYoutubeTagByteLengthValid(chip)) {
        toast.fail(
          `Each YouTube tag must be ${YOUTUBE_TAG_BYTE_MAX} bytes or fewer`
        );
        continue;
      }
      if (chip?.length <= remainingChars && !value.includes(chip)) {
        chipsToAdd?.push(chip);
        remainingChars -= chip?.length;
      }
    }

    if (chipsToAdd?.length > 0) {
      handleChipChange(chipsToAdd, "add");
      setInputValue("");
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target?.value;
    const wouldExceed = committedChars + newValue?.length > totalCharacterLimit;

    if (!wouldExceed) {
      setInputValue(newValue);
      if (newValue?.endsWith(",")) {
        const chipToAdd = newValue?.slice(0, -1).trim();
        if (chipToAdd?.length > 0) {
          addChip(chipToAdd);
        }
      }
    }
  };

  const removeChip = (chipToRemove: string) => {
    if (disabled) return;
    handleChipChange(chipToRemove, "remove");
  };

  const clearAllTags = () => {
    if (disabled) return;
    handleChipChange([], "clear");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions?.length > 0) {
        addChip(filteredSuggestions[highlightedIndex]);
      } else {
        addChip(inputValue.trim());
      }
    } else if (e.key === "Backspace" && inputValue === "") {
      if (value?.length > 0) {
        removeChip(value[value?.length - 1]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex < filteredSuggestions?.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredSuggestions?.length - 1
      );
    }
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const clipboardData = event.clipboardData.getData("text/plain").trim();

    if (!clipboardData) return;

    const chipsToAdd = clipboardData
      .split(",")
      .map((chip) => chip.trim())
      .filter((chip) => chip?.length > 0 && !value?.includes(chip));

    if (chipsToAdd?.length === 0) return;

    const remainingChars = totalCharacterLimit - committedChars;
    let totalLength = 0;
    const validChips = [];
    let hadByteViolation = false;

    for (const chip of chipsToAdd) {
      if (isYoutube && !isYoutubeTagByteLengthValid(chip)) {
        hadByteViolation = true;
        continue;
      }
      if (totalLength + chip?.length <= remainingChars) {
        validChips.push(chip);
        totalLength += chip?.length;
      } else {
        break;
      }
    }

    if (hadByteViolation) {
      toast.fail(
        `Each YouTube tag must be ${YOUTUBE_TAG_BYTE_MAX} bytes or fewer`
      );
    }

    if (validChips.length > 0) {
      handleChipChange(validChips, "add");
      setInputValue("");
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addChip(suggestion);
  };

  const filteredSuggestions = suggestions.filter(
    (suggestion) =>
      suggestion?.toLowerCase()?.includes(inputValue?.toLowerCase()) &&
      !value?.includes(suggestion)
  );

  return (
    <>
      <div className={className}>
        <div className="d-flex align-items-center gap-1 position-relative">
          <label className="form-label d-flex align-items-center">
            {labelCheckbox && !readOnlyMode && (
              <input
                type="checkbox"
                checked={labelCheckboxChecked}
                onChange={(e) => onLabelCheckboxChange?.(e.target.checked)}
                className="me-2"
                style={{ cursor: "pointer" }}
                disabled={disableLabelCheckbox}
              />
            )}
            {label}
            {showAsterisk &&
              (asteriskClassName ? (
                <span
                  className={` ${asteriskClassName}`}
                  style={{ marginLeft: "5px", fontSize: "20px" }}
                />
              ) : (
                <span
                  className="align-middle text-warning"
                  style={{ fontSize: "35px" }}
                >
                  &#8226;
                </span>
              ))}
          </label>
          {showRequired && <span className="required">*</span>}
          <>
            {totalCharacterLimit && (
              <small className="text-muted">
                ( {totalCharsUsed}/{totalCharacterLimit})
              </small>
            )}
            {totalCharsUsed === totalCharacterLimit && (
              <span className="text-danger small">Maximum Limit Reached</span>
            )}
          </>
          {value?.length > 0 && (
            <div
              className={
                fixedLayout
                  ? "d-flex align-items-center gap-2 ms-auto flex-shrink-0"
                  : "d-flex align-items-center gap-2 position-absolute"
              }
              style={fixedLayout ? undefined : { right: "5px", top: "24px" }}
            >
              <i
                className="bi bi-copy fs-5"
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  const result = await copyTextToClipboard(value.join(","));
                  if (result) {
                    toast.success("Tags copied to clipboard");
                  } else {
                    toast.fail("Failed to copy tags to clipboard");
                  }
                }}
                title="Copy all tags"
              />
              {!disabled && (
                <i
                  className="bi bi-x fs-3"
                  style={{ cursor: "pointer" }}
                  onClick={clearAllTags}
                  title="Clear all tags"
                />
              )}
            </div>
          )}
        </div>
        <div
          style={{
            border:
              isFocused && !error
                ? "1px solid #86b7fe"
                : error
                  ? "1px solid #dc3545"
                  : "1px solid var(--bg-input-border)",
            background: "#fff",
            borderRadius: "var(--bs-border-radius)",
          }}
        >
          <div
            className="chip-input-wrapper form-control d-flex flex-wrap gap-1 row-gap-1 border-0 p-0"
            style={fixedLayout ? { width: "100%" } : { width: "90%" }}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
          >
            {Array.isArray(value) &&
              value.map((chip, index) => {
                const exceedsByteLimit =
                  isYoutube && !isYoutubeTagByteLengthValid(chip);
                return (
                  <span
                    key={index}
                    className="d-flex align-items-center px-1 rounded-3 mt-1"
                    title={
                      exceedsByteLimit
                        ? `This tag is ${getByteLength(
                            chip.trim()
                          )} bytes — each YouTube tag must be ${YOUTUBE_TAG_BYTE_MAX} bytes or fewer`
                        : chip
                    }
                    style={{
                      backgroundColor: exceedsByteLimit
                        ? "hsl(0, 85%, 94%)"
                        : "hsl(0, 0%, 90%)",
                      border: exceedsByteLimit
                        ? "1px solid #dc3545"
                        : "1px solid transparent",
                      minHeight: "22px",
                      alignSelf: "center",
                      color: exceedsByteLimit ? "#dc3545" : "#333",
                      marginLeft: "0.375rem",
                      maxWidth: "100%",
                    }}
                  >
                    <span style={{ wordBreak: "break-word" }}>{chip}</span>
                    <i
                      className="bi bi-x ps-1"
                      style={{ cursor: "pointer", flexShrink: 0 }}
                      onClick={() => removeChip(chip)}
                    ></i>
                  </span>
                );
              })}
            <input
              onBlur={onBlur}
              style={{ border: "none", outline: "none", minWidth: "100px" }}
              type="text"
              className="chip-input"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={
                committedChars >= totalCharacterLimit ? undefined : placeholder
              }
              disabled={disabled}
            />
          </div>
        </div>
        {error && <div className="form-text text-danger px-2">{error}</div>}

        {inputValue && filteredSuggestions?.length > 0 && (
          <div className="mt-2 d-flex">
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`badge bg-light text-dark me-2${
                  index === highlightedIndex ? " bg-primary text-white" : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ChipInput;
